/**
 * ★ "PRIMARY LOCATION" (design 25; design 24, T27 - the owner's option 1). The town or city the
 * author is based in, PICKED from the town-and-city search the birth place uses
 * (../data/usePlaceSearch.ts), asked in English because its label is what readers see. It takes the
 * typed "Time zone"'s place: the pick carries the place's zone, which the server copies to the
 * profile's `timezone`, so "we do not write to you at night" keeps working with no IANA name typed.
 * A zone already on file stays until a place is picked.
 *
 * ONLY A PICK IS SENT. The pick goes back whole - `placeId` as a whole number (the search answers it
 * as a string), the label as the search gave it, its zone. Editing the text un-picks it; an empty
 * box clears the location (the zone stays); text that was not picked is refused on screen by the
 * form (../domain/form.ts) and sent as nothing.
 *
 * Public by default, with its own switch under it (`footer`), as the other fields have.
 */
import { Autocomplete, Stack, Text } from '@mantine/core';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { PrimaryLocation } from '../contract';
import type { PrimaryLocationDraft } from '../domain/form';
import { PLACE_SEARCH_MAX, usePlaceSearch } from '../data/usePlaceSearch';
import type { PlaceSuggestion } from '../data/usePlaceSearch';

/** The labels readers see are English, whatever the browser's language. */
const LOCATION_LANGUAGE = 'en';

export interface PrimaryLocationFieldProps {
  readonly id: string;
  readonly value: PrimaryLocationDraft;
  readonly error?: string | undefined;
  /** The public switch. */
  readonly footer?: ReactNode;
  readonly onChange: (next: PrimaryLocationDraft) => void;
}

export function PrimaryLocationField({
  id,
  value,
  error,
  footer,
  onChange,
}: PrimaryLocationFieldProps): ReactNode {
  const { t } = useTranslation('application');
  const picked: PlaceSuggestion | null =
    value.picked === null
      ? null
      : {
          birthPlaceId: String(value.picked.placeId),
          birthPlace: value.picked.label,
          birthTimeZone: value.picked.timeZone,
        };
  const search = usePlaceSearch(value.text, picked, LOCATION_LANGUAGE);
  // The place just chosen, for the text change the list sends right after the choice: that change
  // carries the place's own label and must not un-pick what was just picked.
  const chosen = useRef<PrimaryLocation | null>(null);
  return (
    <Stack gap={6} id={id}>
      <Autocomplete
        label={t('location.label')}
        description={t('location.description')}
        placeholder={t('location.placeholder')}
        // The suggestions are the platform's, already matched - this field filters nothing.
        data={search.places.map((place) => ({
          value: place.birthPlaceId,
          label: place.birthPlace,
        }))}
        filter={({ options }) => options}
        value={value.text}
        maxLength={PLACE_SEARCH_MAX}
        error={error}
        onChange={(text) => {
          // Editing a picked place un-picks it; picking one sets the text to its own label.
          const kept = [value.picked, chosen.current].find((place) => place?.label === text);
          onChange({ text, picked: kept ?? null });
        }}
        onOptionSubmit={(birthPlaceId) => {
          const place = search.places.find((candidate) => candidate.birthPlaceId === birthPlaceId);
          if (place !== undefined) {
            const pick: PrimaryLocation = {
              placeId: Number(place.birthPlaceId),
              label: place.birthPlace,
              timeZone: place.birthTimeZone,
            };
            chosen.current = pick;
            onChange({ text: place.birthPlace, picked: pick });
          }
        }}
      />
      {search.status === 'failed' ? (
        <Text size="xs" c="dimmed" role="status">
          {t('location.searchFailed')}
        </Text>
      ) : null}
      {search.status === 'ready' && search.places.length === 0 && value.picked === null ? (
        <Text size="xs" c="dimmed" role="status">
          {t('location.noMatch')}
        </Text>
      ) : null}
      <div>{footer}</div>
    </Stack>
  );
}
